import {sortBy} from 'lodash';
import {ParsedUrlQuery} from 'querystring';

import {
  Order,
  ProductCategoryEnum,
  ProductPackageTypeEnum,
  ProductVariant,
  Subscription,
} from '#apollo/schema';
import {EventType, useAnalytics, UserActionEvent} from '#component/provider/AnalyticsProvider';
import {
  MEMBER_TID,
  MEMBER_VARIANT_TID,
  membershipVariantIds,
  supplements,
} from '#constant/products';
import {Colors} from '#theme';
import {
  CustomerTypeEnum,
  getProductVariantIdByVariantTerm,
  getProductVariantIdsByTerm,
} from '#util/subscriptions';

import {ProductVariantConfirmation} from './components/SubscriptionManagerCard/components/UpdateConfirmationModal/UpdateConfirmationModal.types';
import {useSubscriptionManager} from './SubscriptionManagerContext';

import {
  CustomerType,
  DiscountRule,
  GetHighestPricedSupplementTidProps,
  GetSubscriptionManagerCardDataProps,
  GetSubscriptionManagerCardDataReturn,
  ManagePlanParams,
  ManageProductType,
  PromoCodeAction,
  SubscriptionManagerState,
  SupplementRenderListProps,
  TransformVariantToProductProps,
} from './SubscriptionManager.types';

export const useGetSubscriptionManagerCardData = ({
  customerType,
  state,
  productVariants,
}: GetSubscriptionManagerCardDataProps): GetSubscriptionManagerCardDataReturn => {
  const {
    initialState,
    setInitialState,
    pendingChanges,
    setPendingChanges,
    selectedSupplements,
    selectedSupplementsCount,
    onChangeSubscription,
    onChangeSupplement,
    appliedPromoCodes,
    discountsToSimulate,
    updatePromoCodes,
    openUpdateConfirmationModal,
    setOpenUpdateConfirmationModal,
    hasAutoAppliedPromoCodes,
    setHasAutoAppliedPromoCodes,
    alreadyAppliedPromoCodeError,
    setAlreadyAppliedPromoCodeError,
    setHasAutoAppliedPromoCodesError,
  } = useSubscriptionManager();
  const membershipVariantIds = getProductVariantIdsByTerm(productVariants, MEMBER_TID);
  const {track} = useAnalytics();

  const handleTrackClick = (actionName: string) => {
    track<UserActionEvent>({
      evtName: EventType.UserAction,
      actionName,
    });
  };

  const activeSubscription = state !== SubscriptionManagerState.DESELECTED;

  const title =
    customerType.toLowerCase() === CustomerTypeEnum.membership
      ? 'Membership'
      : 'Supplement Subscription';

  const discount = calculateDiscount(customerType, selectedSupplementsCount);

  const isDiscounted = discount > 0;

  const textColor: Colors = isDiscounted
    ? activeSubscription
      ? 'dustyGray'
      : 'deepBurgundy'
    : 'doveGray';

  const totalPendingChangesValue =
    pendingChanges?.products.reduce((total, product) => {
      return total + product.price;
    }, 0) || 0;

  const membershipPVPending = pendingChanges?.products.find(product =>
    membershipVariantIds.includes(product.variantId),
  );

  const membershipProductVariant = productVariants.find(pv => pv.tid === MEMBER_VARIANT_TID);

  const supplementTotalValue = totalPendingChangesValue;
  let membershipTotalValue = totalPendingChangesValue;
  if (!membershipPVPending) {
    // If Membership is not in the customer's pending changes, add the membership price to membershipTotalValue
    membershipTotalValue += membershipProductVariant?.price ?? 0;
  }

  // Find the highest priced supplement to include that in the Membership pricing
  let highestPricedSupplementValue = 0;
  let highestPricedSupplementVariantId = '';

  if (selectedSupplementsCount >= 1) {
    Object.keys(selectedSupplements).forEach(ssKey => {
      if (selectedSupplements[ssKey] && !membershipVariantIds.includes(ssKey)) {
        const supplementPV = productVariants.find(pv => pv.id === ssKey);
        if ((supplementPV?.price ?? 0) > highestPricedSupplementValue) {
          highestPricedSupplementValue = supplementPV?.price ?? 0;
          highestPricedSupplementVariantId = ssKey;
        }
      }
    });
  }

  membershipTotalValue -= highestPricedSupplementValue;

  // Applying discounts based on supplement count to calculate the total discounted price
  let supplementTotalValueDiscounted = supplementTotalValue;
  let membershipTotalValueDiscounted = membershipTotalValue;
  if (selectedSupplementsCount >= 1) {
    let additionalSupplementsTotal = 0;
    Object.keys(selectedSupplements).forEach(ssKey => {
      if (selectedSupplements[ssKey] && ssKey !== highestPricedSupplementVariantId) {
        const supplementPV = productVariants.find(pv => pv.id === ssKey);
        if (supplementPV) {
          additionalSupplementsTotal += supplementPV.price;
        }
      }
    });

    membershipTotalValueDiscounted =
      membershipTotalValue - (additionalSupplementsTotal * discount) / 100;
    supplementTotalValueDiscounted = supplementTotalValue - (supplementTotalValue * discount) / 100;
  }

  const supplementRenderList: ManageProductType[] = getSupplementsForSubscriptionManagerCard({
    productVariants,
    activeSubscription,
    customerType,
    selectedSupplements,
  });

  const startingAtValue = getStartingAtValue(customerType, productVariants, supplementRenderList);

  const undoChanges = () => {
    handleTrackClick('Manage My Plan - Click Undo Changes');
    setPendingChanges(initialState);
    updatePromoCodes('', PromoCodeAction.CLEAR);
  };

  return {
    supplementTotalValue,
    supplementTotalValueDiscounted,
    membershipTotalValue,
    membershipTotalValueDiscounted,
    textColor,
    isDiscounted,
    title,
    activeSubscription,
    discount,
    supplements,
    selectedSupplements,
    onChangeSupplement,
    selectedSupplementsCount,
    initialState,
    setInitialState,
    pendingChanges,
    undoChanges,
    supplementRenderList,
    onChangeSubscription,
    startingAtValue,
    handleTrackClick,
    appliedPromoCodes,
    discountsToSimulate,
    openUpdateConfirmationModal,
    setOpenUpdateConfirmationModal,
    hasAutoAppliedPromoCodes,
    setHasAutoAppliedPromoCodes,
    alreadyAppliedPromoCodeError,
    setAlreadyAppliedPromoCodeError,
    setHasAutoAppliedPromoCodesError,
  };
};

const getSupplementsForSubscriptionManagerCard = ({
  productVariants,
  activeSubscription,
  customerType,
  selectedSupplements,
}: SupplementRenderListProps): ManageProductType[] => {
  const supplementVariants = productVariants?.filter(
    variant =>
      variant.product?.category.toLowerCase() == ProductCategoryEnum.Supplement &&
      variant.packageType?.toLowerCase() == ProductPackageTypeEnum.Bottle,
  );

  const selectedVariantTids = Object.keys(selectedSupplements).filter(
    variantId => selectedSupplements[variantId],
  );
  const selectedVariants = supplementVariants
    ?.filter(variant => selectedVariantTids.includes(variant.id))
    .map(variant =>
      transformVariantToProduct({
        variant,
        supplement: supplements.find(supplement => supplement.variantTid === variant.tid),
      }),
    );

  const highestPricedSupplementId = getHighestPricedSupplementTid({
    selectedVariants,
    activeSubscription,
    customerType,
    productVariants,
  });

  const mappedSupplements = supplementVariants?.map(supplementVariant => {
    const supplement = supplements.find(
      supplement => supplement.variantTid === supplementVariant.tid,
    );

    return transformVariantToProduct({
      variant: supplementVariant,
      supplement,
      membershipIncluded:
        customerType.toLowerCase() === CustomerTypeEnum.membership &&
        highestPricedSupplementId === supplementVariant.id,
    });
  });
  const orderedSupplements = sortBy(mappedSupplements, [
    supplement => supplement.displayOrder || 0,
  ]);

  return orderedSupplements;
};

export const getHighestPricedSupplementTid = ({
  selectedVariants,
  activeSubscription,
  customerType,
  productVariants,
}: GetHighestPricedSupplementTidProps): string | null => {
  const membershipVariantIds = getProductVariantIdsByTerm(productVariants, MEMBER_TID);

  if (
    customerType.toLowerCase() !== CustomerTypeEnum.membership ||
    !activeSubscription ||
    selectedVariants.length === 0
  ) {
    return null;
  }

  const filteredVariants = selectedVariants.filter(
    product => !membershipVariantIds.includes(product.variantId),
  );

  filteredVariants.sort((a, b) => {
    if (a.price !== b.price) {
      return b.price - a.price;
    }
    return a.variantId.localeCompare(b.variantId);
  });

  return filteredVariants.length > 0 ? filteredVariants[0].variantId : null;
};

const getStartingAtValue = (
  customerType: CustomerType,
  productVariants: ProductVariant[],
  supplementRenderList: ManageProductType[],
): number => {
  const membershipVariantIds = getProductVariantIdsByTerm(productVariants, MEMBER_TID);
  const membershipVariantId = getMembershipVariantId(productVariants);
  if (customerType.toLowerCase() === CustomerTypeEnum.membership) {
    const membershipVariant = productVariants.find(variant => membershipVariantId === variant.id);
    return membershipVariant?.price || 0;
  }

  const startingAtValue = supplementRenderList.reduce(
    (acc: number, supVariant: ManageProductType) => {
      if (membershipVariantIds.includes(supVariant.variantId)) {
        return acc;
      }
      return supVariant.price < acc ? supVariant.price : acc;
    },
    999999,
  );

  return startingAtValue;
};

export const transformVariantToProduct = ({
  variant,
  supplement,
  membershipIncluded = false,
}: TransformVariantToProductProps): ManageProductType => ({
  tid: variant.tid,
  variantId: variant.id,
  membershipIncluded,
  price: variant.price,
  imageURL: supplement ? supplement.managePlanImage : variant.product?.thumbnail || '',
  title: supplement ? supplement.title : variant.product?.title || '',
  displayOrder: variant.product?.displayOrder || 0,
  category: variant.product?.category || ProductCategoryEnum.Other,
});

export const getLastOrderDate = (orders: Partial<Order>[]): Date | null => {
  if (!orders.length) {
    return null;
  }
  return orders.reduce((acc, order) => {
    const orderDate = new Date(order.billingDate);
    return orderDate > acc ? orderDate : acc;
  }, new Date());
};

export const mapProductsToVariants = (
  manageProducts: ManageProductType[],
  productVariants: ProductVariant[],
): ProductVariantConfirmation[] => {
  return manageProducts.map(product => {
    const matchedProduct = productVariants.find(pv => pv.id === product.variantId);
    if (matchedProduct) {
      return {
        ...product,
        tid: matchedProduct.tid,
        id: product.variantId,
      } as ProductVariantConfirmation;
    } else {
      return {
        ...product,
        tid: '',
        id: product.variantId,
      } as ProductVariantConfirmation;
    }
  });
};

export const isPendingMembership = (
  subscription: Subscription,
  hasMembership: boolean,
  membershipProductVariantIds: string[] = [],
): boolean => {
  const hasMembershipUpcoming = subscription.items?.some(item =>
    membershipProductVariantIds.includes(String(item.variantId)),
  );
  return hasMembership && !hasMembershipUpcoming;
};

export const parseQueryParams = (query: ParsedUrlQuery): ManagePlanParams => {
  const add = query.add as string;
  const discount = query.discount as string;

  return {
    ...(add ? {add} : {}),
    ...(discount ? {discount} : {}),
  };
};

const discountRules: Record<CustomerType, DiscountRule[]> = {
  membership: [{minCount: 2, discount: 15}],
  subscription: [{minCount: 1, discount: 15}],
};

const calculateDiscount = (
  customerType: CustomerType,
  selectedSupplementsCount: number,
): number => {
  const rules = discountRules[customerType];

  for (const rule of rules) {
    const {minCount, discount} = rule;

    if (selectedSupplementsCount >= minCount) {
      return discount;
    }
  }

  return 0;
};

export const getMembershipVariantId = (productVariants: ProductVariant[]): string | null => {
  let membershipVariantId = null;
  for (const id of membershipVariantIds) {
    const variantId = getProductVariantIdByVariantTerm(productVariants, id);
    if (variantId) {
      membershipVariantId = variantId;
      break;
    }
  }
  return membershipVariantId;
};
